import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {boolean} */
  #skippedInitialPrepend = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * Memory: max pages of cards kept in the live DOM at once. Older pages that scroll far above the
   * viewport are replaced by a same-height spacer (their card DOM/JS/images are freed) and restored
   * when scrolled back near. Bounds memory on long collections for low-RAM devices (iPhone 13 / 4GB)
   * without changing the infinite-scroll UX or pagination.
   */
  #maxRenderedPages = 2;

  /** @type {IntersectionObserver | undefined} Restores a recycled page's cards when its spacer nears the viewport. */
  #spacerObserver;

  /**
   * @type {Map<HTMLElement, HTMLElement[]>} spacer element -> the removed card ELEMENTS (for restore).
   * The live elements (not serialized HTML) are kept so listeners, component state and asynchronously
   * hydrated content (e.g. the Reviews.io star widget, which holds references to these nodes) survive a
   * recycle/restore round-trip. Detached subtrees still release their decode/layout/compositing memory,
   * which is the cost this windowing exists to bound.
   */
  #recycledPages = new Map();

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Restore recycled (spacer'd) pages well before they reach the viewport when scrolling up.
    this.#spacerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#restoreRecycledPage(/** @type {HTMLElement} */ (entry.target));
        }
      },
      { rootMargin: '1200px 0px' }
    );

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    if (this.#spacerObserver) {
      this.#spacerObserver.disconnect();
    }
    // Put recycled cards back before dropping our references: if this element is ever disconnected
    // and later reconnected with its DOM intact, no page may be stranded as a spacer whose cards
    // were garbage-collected. (When the whole subtree is being discarded this is harmless.)
    for (const [spacer, cards] of this.#recycledPages) {
      if (spacer.isConnected && cards?.length) {
        spacer.before(...cards);
        spacer.remove();
      }
    }
    this.#recycledPages.clear();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    // Update URL immediately so it reflects the page as soon as the user scrolls
    history.pushState('', '', nextPage.url.toString());

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Memory: free pages that have scrolled far above the viewport (replace with a same-height spacer).
    this.#recycleOffscreenPages();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Replaces the card DOM of pages that have scrolled far above the viewport with a same-height
   * spacer, freeing their images/components. The spacer keeps the document height unchanged (so the
   * scroll position never jumps, even on iOS which lacks scroll-anchoring) and is restored to its
   * cards by #spacerObserver when scrolled back near.
   */
  #recycleOffscreenPages() {
    const { grid } = this.refs;
    if (!grid) return;

    const cards = /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > [ref="cards[]"]')]);
    if (!cards.length) return;

    // Group consecutive cards by their data-page, preserving DOM order.
    /** @type {{ page: string, cards: HTMLElement[] }[]} */
    const groups = [];
    for (const card of cards) {
      const page = card.dataset.page ?? '';
      const last = groups[groups.length - 1];
      if (last && last.page === page) last.cards.push(card);
      else groups.push({ page, cards: [card] });
    }

    // Recycle the top-most group(s) while we exceed the budget AND they are safely above the viewport.
    // The distance must exceed #spacerObserver's 1200px restore rootMargin (hysteresis), otherwise a
    // just-recycled page whose spacer lands inside the restore margin is immediately restored again —
    // recycle/restore churn with no memory benefit.
    while (groups.length > this.#maxRenderedPages) {
      const top = groups[0];
      const lastCard = top.cards[top.cards.length - 1];
      // Only recycle when the whole group is comfortably beyond the restore margin.
      if (lastCard.getBoundingClientRect().bottom > -2000) break;
      this.#recyclePageGroup(top.cards);
      groups.shift();
    }
  }

  /**
   * @param {HTMLElement[]} groupCards - The contiguous cards of one page to recycle.
   */
  #recyclePageGroup(groupCards) {
    const first = groupCards[0];
    const last = groupCards[groupCards.length - 1];
    const height = last.getBoundingClientRect().bottom - first.getBoundingClientRect().top;
    if (height <= 0) return;

    const spacer = document.createElement('li');
    spacer.className = 'paginated-list__recycled-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    // Stamp the page so #getPage('previous') can see the true top boundary of the rendered window
    // while this page is recycled (prevents prepending a page that is already held in a spacer).
    if (first.dataset.page) spacer.dataset.page = first.dataset.page;
    spacer.style.gridColumn = '1 / -1';
    spacer.style.height = `${height}px`;
    spacer.style.pointerEvents = 'none';

    for (const card of groupCards) {
      // Preserve each card's real rendered size for content-visibility while it is detached: restored
      // cards re-enter the DOM above the viewport in the "skipped" state, and without this they would
      // lay out at the stylesheet's generic intrinsic-size seed — changing the document height vs the
      // spacer they replace and jumping the scroll position on iOS (which lacks scroll anchoring).
      const rect = card.getBoundingClientRect();
      if (rect.width > 0) card.style.setProperty('contain-intrinsic-width', `auto ${Math.round(rect.width)}px`);
      if (rect.height > 0) card.style.setProperty('contain-intrinsic-height', `auto ${Math.round(rect.height)}px`);
    }

    // Keep the live elements (state, listeners, hydrated widgets intact) for restore on scroll-up.
    this.#recycledPages.set(spacer, groupCards);

    first.before(spacer);
    for (const card of groupCards) card.remove();

    this.#spacerObserver?.observe(spacer);
  }

  /**
   * @param {HTMLElement} spacer - A recycled-page spacer to restore back to its cards.
   */
  #restoreRecycledPage(spacer) {
    const cards = this.#recycledPages.get(spacer);
    if (!cards?.length) return;

    this.#spacerObserver?.unobserve(spacer);
    this.#recycledPages.delete(spacer);

    // Re-insert the original elements. Their inline contain-intrinsic-* (stamped at recycle time)
    // makes the restored block occupy the same height as the spacer even while content-visibility
    // still skips their rendering — so the document height is stable and the scroll never jumps.
    spacer.before(...cards);
    spacer.remove();

    this.#aspectRatioHelper?.processNewElements();
  }

  async #renderPreviousPage() {
    const { grid, cards } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    // Don't prepend on initial load when user landed directly on a paginated URL (e.g. ?page=2).
    // Otherwise the sentinel at the top triggers and overwrites the URL/content. Only skip once.
    if (!this.#skippedInitialPrepend) {
      const urlPage = this.#getPageFromUrl();
      const firstCard = Array.isArray(cards) ? cards[0] : undefined;
      const firstCardPage = firstCard ? Number(firstCard.dataset.page) : undefined;
      const isDirectLoadOfPaginatedPage =
        typeof urlPage === 'number' &&
        urlPage > 1 &&
        Number.isFinite(firstCardPage) &&
        firstCardPage === urlPage;
      if (isDirectLoadOfPaginatedPage) {
        this.#skippedInitialPrepend = true;
        return;
      }
    }

    // Update URL immediately so it reflects the page as soon as the user scrolls
    history.pushState('', '', previousPage.url.toString());

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @returns {number | undefined} Current page from URL, or undefined if not set/invalid
   */
  #getPageFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const p = params.get('page');
    if (p === null) return undefined;
    const n = parseInt(p, 10);
    return Number.isFinite(n) && n >= 1 ? n : undefined;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid, cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    let targetCard = cards[isPrevious ? 0 : cards.length - 1];

    // The top boundary must include pages currently held in recycled spacers (which sit above the
    // first live card and carry data-page). Otherwise a fast jump to the top can fire the previous-page
    // sentinel before the spacers restore, and we would prepend a page that already exists as a spacer
    // — duplicating its products and rewriting the URL to the wrong page.
    if (isPrevious && grid && this.#recycledPages.size > 0) {
      const topmost = /** @type {HTMLElement | null} */ (grid.querySelector(':scope > [data-page]'));
      if (topmost) targetCard = topmost;
    }

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Drop any recycled-page spacers (the filtered result is a fresh list).
    for (const spacer of this.#recycledPages.keys()) {
      this.#spacerObserver?.unobserve(spacer);
      spacer.remove();
    }
    this.#recycledPages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
